import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Typography, Breadcrumbs, Link, Box } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { extractBestTranslation } from '@/utils/helpers';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useAuth } from '@contexts/AuthContext';
import API from '@utils/axios';
import Layout from '@components/Layout';
import WorkflowLoading from '@components/workflow/WorkflowLoading';
import WorkflowForm from '@components/workflow/WorkflowForm';

function WorkflowEditPage({ type }) {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const { showMessage } = useSnackbar();
    const { refreshAuth } = useAuth();
    const [initialData, setInitialData] = useState(null);
    const [loaded, setLoaded] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const response = await API.get(`/workflows/${id}`);
            setInitialData(response.data);
        } catch (error) {
            showMessage(t('Failed to load data'), 'error');
        } finally {
            setLoaded(true);
        }
    }, [id, t, showMessage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (data) => {

        const showWarnings = (warnings) => {
            warnings.forEach(warning => {
                if (typeof warning === 'string') {
                    showMessage(t(warning), 'warning');
                } else if (typeof warning === 'object' && warning.message) {
                    showMessage(t(warning.message, warning.data || {}), 'warning');
                }
            });
        }

        try {
            const result = await API.put(`/workflows/${id}`, data);
            
            // Mise à jour des données avec la réponse complète de l'API (incluant le BPMN potentiellement modifié)
            setInitialData(result.data);
            
            // Affichage du message de succès
            showMessage(t('Workflow updated successfully'), 'success');
            
            // Affichage des warnings s'il y en a
            showWarnings(result.data.warnings);
            
            refreshAuth();
        } catch (error) {
            if (error.response && error.response.status === 422 && error.response.data.message) {
                showMessage(t('Unable to save the workflow:')  + ' ' + t(error.response.data.message), 'error');
            } else {
                showMessage(error.message, 'error');
            }
            showWarnings(error.response.data.warnings);
        }
    };

    return (
        <Layout>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link component={RouterLink} to={`/workflows/${type}`} underline="hover">{t('Workflow management')}</Link>
                    {type === 'custom'
                        ? <Link component={RouterLink} to="/workflows/custom" underline="hover">{t('Custom workflows')}</Link>
                        : <Link component={RouterLink} to="/workflows/templates" underline="hover">{t('Templates')}</Link>
                    }
                    <Typography>{initialData ? extractBestTranslation(initialData.name, i18n.language) : ''}</Typography>
                </Breadcrumbs>
                <Link component={RouterLink} to={`/workflows/${type}`} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5 }} />
                    {t('Back to list')}
                </Link>
            </Box>
            {loaded ?
                <WorkflowForm initialData={initialData} handleSubmit={handleSubmit} />
                : <WorkflowLoading />
            }
        </Layout>
    );
}

export default WorkflowEditPage;
